import { useState } from "react";
import { ArrowRight, CheckCircle2, HelpCircle, ChevronDown } from "lucide-react";

const girribals = [
  {
    title: "Girribal 1 — Story",
    teaching:
      "Bigibila is not just a name. Bigibila carries story, old people, responsibility and protection.",
    question: "What does Bigibila carry?",
    options: [
      "Only a logo",
      "Story, responsibility and protection",
      "A normal software password",
    ],
    answer: "Story, responsibility and protection",
  },
  {
    title: "Girribal 2 — Protection",
    teaching:
      "Bigibila's quill is the protection mark. It carries boundary, consequence, survival and governance.",
    question: "What does Bigibila's quill mean?",
    options: [
      "Decoration",
      "Protection, boundary and consequence",
      "A file icon",
    ],
    answer: "Protection, boundary and consequence",
  },
  {
    title: "Girribal 3 — Governance",
    teaching:
      "Aboriginal governance is not something added onto systems. It is a governance standard systems should become accountable to.",
    question: "What should systems become accountable to?",
    options: [
      "Aboriginal governance",
      "Only dashboards",
      "Generic policy templates",
    ],
    answer: "Aboriginal governance",
  },
  {
    title: "Girribal 4 — Data Sovereignty",
    teaching:
      "Indigenous Data Sovereignty means data must respect authority, provenance, custodianship, purpose and community responsibility.",
    question: "What must data respect?",
    options: [
      "Only storage location",
      "Authority, provenance, custodianship and purpose",
      "Only government reporting",
    ],
    answer: "Authority, provenance, custodianship and purpose",
  },
  {
    title: "Girribal 5 — Responsibility",
    teaching:
      "No sharing without authority, purpose, permission and record.",
    question: "What must exist before sharing?",
    options: [
      "Authority, purpose, permission and record",
      "A quick email",
      "A public download link",
    ],
    answer: "Authority, purpose, permission and record",
  },
];

export default function GirribalAccessGate({ onEnter }) {
  const [step, setStep] = useState(0);
  const [selected, setSelected] = useState("");
  const [error, setError] = useState("");
  const [showAbout, setShowAbout] = useState(false);

  const current = girribals[step];

  function handleAnswer(option) {
    setSelected(option);
    setError("");

    if (option === current.answer) {
      setTimeout(() => {
        if (step === girribals.length - 1) {
          onEnter();
        } else {
          setStep(step + 1);
          setSelected("");
        }
      }, 500);
    } else {
      setError("Not yet. Read the teaching and try again.");
    }
  }

  return (
    <div
      className="min-h-screen bg-[#FAFAF9] text-stone-900 flex items-center justify-center px-4 py-10 relative overflow-hidden"
      data-testid="girribal-access-gate"
    >
      {/* Subtle dotted topology backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.5]"
        style={{
          backgroundImage:
            "radial-gradient(rgba(28,25,23,0.07) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-3xl w-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-full bg-stone-900 flex items-center justify-center overflow-hidden">
            <img src="/brand/bigibila-logo.png" alt="Bigibila Systems" className="h-11 w-11 object-contain" />
          </div>
          <div className="leading-none">
            <div className="font-display font-bold tracking-tight text-[15px] text-stone-900">
              BIGIBILA
            </div>
            <div className="text-[9px] tracking-[0.32em] uppercase text-stone-500 mt-0.5">
              Systems™ Access
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5">
          <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221] inline-flex items-center gap-2">
            <span className="h-1.5 w-1.5 bg-[#9C4221] rounded-full" />
            Yaamagara
          </span>
          <span className="text-stone-500 text-[11px] italic">"How are you" in Gamilaraay</span>
          <span className="hidden md:inline text-stone-300">·</span>
          <span className="text-[10px] tracking-[0.28em] uppercase font-bold text-stone-500">
            Learning gate
          </span>
        </div>

        <h1 className="font-display text-5xl md:text-6xl tracking-tighter font-bold text-stone-900 leading-[0.95]">
          Pass through<br />
          <span className="text-stone-400">the Girribals.</span>
        </h1>

        <p className="mt-6 text-stone-700 leading-relaxed max-w-2xl text-lg">
          This is not a password gate. It is a learning gate. To enter BIGIBILA SYSTEMS™, you must first understand the responsibility of story, protection, governance and data sovereignty.
        </p>

        {/* What is a Girribal? — inline disclosure */}
        <div className="mt-5 max-w-2xl">
          <button
            type="button"
            onClick={() => setShowAbout((v) => !v)}
            data-testid="girribal-about-toggle"
            className="inline-flex items-center gap-2 text-sm text-stone-700 hover:text-[#9C4221] transition-colors border-b border-stone-300 hover:border-[#9C4221] pb-0.5"
            aria-expanded={showAbout}
          >
            <HelpCircle className="h-4 w-4" />
            What is a Girribal?
            <ChevronDown className={`h-4 w-4 transition-transform ${showAbout ? "rotate-180" : ""}`} />
          </button>
          {showAbout && (
            <div
              className="mt-4 border border-stone-200 bg-white/60 p-5 text-sm leading-relaxed text-stone-700"
              data-testid="girribal-about-panel"
            >
              <p>
                A <span className="font-semibold text-stone-900">girribal</span> is a traditional riddle, puzzle, or question-and-answer game used in <span className="italic">Yuwaalaraay / Yuwaalayaay / Gamilaraay</span> communities to share knowledge, test observation, encourage critical thinking and strengthen community connection through storytelling, performance and humour.
              </p>
              <p className="mt-3">
                Here, a girribal becomes a <span className="font-semibold text-stone-900">knowledge gate</span>. Instead of typing a password, you read a teaching and answer a question. Each correct answer earns the next passage.
              </p>
              <p className="mt-3 text-stone-500 text-xs italic">
                Access is earned through learning, not by entering a password.
              </p>
            </div>
          )}
        </div>

        {/* Teaching panel — accent ochre/terracotta on cream */}
        <div
          className="mt-10 border border-[#9C4221]/30 bg-[#9C4221]/[0.06] p-7"
          data-testid={`girribal-teaching-${step}`}
        >
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#9C4221]">
            {current.title}
          </div>
          <p className="mt-4 text-stone-900 leading-[1.85] text-lg max-w-2xl">
            {current.teaching}
          </p>
        </div>

        {/* Question + options */}
        <div className="mt-10">
          <h2 className="font-display text-2xl md:text-3xl tracking-tight font-bold text-stone-900">
            {current.question}
          </h2>

          <div className="mt-6 grid gap-3">
            {current.options.map((option) => {
              const isCorrectSelection = selected === option && option === current.answer;
              return (
                <button
                  key={option}
                  onClick={() => handleAnswer(option)}
                  data-testid={`girribal-option-${step}-${option.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`group text-left border px-5 py-4 transition-all flex items-center justify-between gap-3 ${
                    isCorrectSelection
                      ? "border-[#9C4221] bg-[#9C4221]/[0.08] text-stone-900"
                      : "border-stone-300 bg-[#FAFAF9] text-stone-800 hover:border-stone-900 hover:-translate-y-0.5"
                  }`}
                >
                  <span className="text-sm md:text-base">{option}</span>
                  {isCorrectSelection ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-[#9C4221]" strokeWidth={2.2} />
                  ) : (
                    <ArrowRight className="h-4 w-4 shrink-0 text-stone-400 group-hover:text-stone-900 transition-colors" />
                  )}
                </button>
              );
            })}
          </div>

          {error && (
            <div
              className="mt-5 border border-rose-200 bg-rose-50 text-rose-900 p-4 text-sm"
              data-testid="girribal-error"
            >
              {error}
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="mt-12 flex items-center justify-between text-xs">
          <span className="text-stone-500" data-testid="girribal-progress-text">
            Girribal {step + 1} of {girribals.length}
          </span>
          <span className="text-[10px] tracking-[0.24em] uppercase font-bold text-stone-500">
            Public · Protected · Restricted
          </span>
        </div>
        <div className="mt-3 h-1 bg-stone-200 overflow-hidden">
          <div
            className="h-full bg-[#9C4221] transition-all duration-500"
            style={{ width: `${((step + 1) / girribals.length) * 100}%` }}
            data-testid="girribal-progress-bar"
          />
        </div>

        {/* Footer protective notice */}
        <p className="mt-10 text-xs text-stone-500 leading-relaxed max-w-2xl">
          BIGIBILA SYSTEMS™ is not a database of culture. It is governance architecture for authority, permission, protection, accountability and evidence. Background IP applies.
        </p>
      </div>
    </div>
  );
}
